import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import './Auth.css';
import '../../pages/Pages.css';
import artfooter from '../home/image/artfooter.png';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const recaptcha = useRef();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract token from URL on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    
    if (!resetToken) {
      setError('Invalid or missing reset token. Please check your email link.');
    } else {
      setToken(resetToken);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const { password, confirmPassword } = formData;
    
    // Form validation
    if (!password || !confirmPassword) {
      setError(t('please_fill_all_fields'));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('passwords_do_not_match'));
      return;
    }
    
    if (password.length < 6) {
      setError(t('password_min_length'));
      return;
    }
    
    // Verify reCAPTCHA
    const captchaValue = recaptcha.current.getValue();
    if (!captchaValue) {
      setError(t('recaptcha_not_ready'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Send request to reset password with reCAPTCHA token
      await axios.post('users/reset-password', {
        token,
        password,
        recaptchaToken: captchaValue
      });
      
      // Show success message
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            passwordReset: true
          } 
        });
      }, 3000);
      
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response?.data?.error?.code === 'RECAPTCHA_FAILED') {
        setError(t('recaptcha_verification_failed'));
      } else {
        setError(err.response?.data?.error || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="logo-container logo-ellipse">
          <img src={artfooter} alt={t('studio_name')} className="footer-logo-img" />
        </div>
        
        <p className="welcome-text">{t('studio_name')}</p>
        
        <h2>{t('reset_password')}</h2>
        
        {!token && !loading && (
          <div className="error-message">
            {error || 'Invalid or missing reset token. Please check your email link.'}
          </div>
        )}
        
        {error && token && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {success ? (
          <div className="success-message">
            <p>{t('password_reset_success')}</p>
            <p>{t('redirecting_to_login')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">{t('new_password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('enter_new_password')}
                required
                minLength="6"
                disabled={!token || loading}
              />
              <small>{t('password_min_length_hint')}</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('confirm_password')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('confirm_new_password')}
                required
                disabled={!token || loading}
              />
            </div>
            
            <div className="form-group recaptcha-container">
              <ReCAPTCHA
                ref={recaptcha}
                sitekey="6LdfTT8rAAAAAM_TkD9dKutCyy3LJ4KHyINaRWtI"
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={!token || loading}
            >
              {loading ? t('resetting_password') : t('reset_password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword; 